// Vue
import Vue from "vue";
import { mapGetters } from "vuex";

// Services
import { ObjectUtil,
  ShoppingCartService, 
  ErrorLogService} from "@/services";

// Mixins
import { formatDateTime, stringHelper } from "@/components/mixins";

// Types
import { IOrderResponseDto, OrderPlacementStatus, OrderType } from "@/types";
import { CartStepName, IShoppingCartContext } from "../Types";
import { IEmptyCartRequest } from "@/services/ShoppingCartService";
import { PartsStoreOrdersService } from "@/services/PartsStoreOrdersService";

//Components
import DontMissCard  from "./DontMissCard/DontMissCard.vue"

export default Vue.extend({
  name: "ShoppingCartThankYou",
  mixins: [formatDateTime, stringHelper],
  components: {
    DontMissCard
  },
  computed: {
    ...mapGetters([
      "user", 
      "activeAccountId",
      "getContentItem", 
      "IsMcfa",
      "McfCostCenterId",      
      "getRecentComponentName",
      "getRecentComponentData"
    ]),

    emailAddress() {
      const ctx = this.context as IShoppingCartContext;

      return ctx?.user?.Email ?? "";
    },

    poNumber(){
      var orderSummary = this.context.orderSummary;
      //There's only one PO number per order - return first PO found so it can be used for each order line
      for(var i = 0; i < orderSummary.length; i++){
        if(orderSummary[i].PONumber){
          return orderSummary[i].PONumber;
        }
      }
      return "";
    },

    items() {
      const ctx = this.context as IShoppingCartContext;
      return ctx.orderSummary?.map((item) => {
        const orderType = this.getOrderType(item);
        const orderNumber = item.OrderNumber === "" ? "Pending" : item.OrderNumber;
        const poNumber = item.PONumber;
        const status = this.getStatusLabel(item);

        return {
          orderType,
          orderNumber,
          poNumber,
          status,
        };
      });
    },
  },
  props: {
    context: {
      type: Object,
      default: () => ({})
    }
  },
  created(){
    if(_.isEmpty(this.context) && !this.$route.query.forceLoad)
    {
      this.$router.push({ name: 'Home'})
    }

    // Parts Store Order Customer
    if(this.getRecentComponentName === "PartsStoreOrderDetails"){
      this.FinishProcessingPartsStoreOrder();
    }

    this.clearCartOnSuccess();
  },
  data: () => ({
    dontMissCards:[
      {
        cardNumber: 1,
        link: "https://www.mcfadealers.com/en/parts-and-service/parts/parts-policies",
        title: "Part Policies",
        icon: "dnIconPartPolicy"
      },
      {
        cardNumber: 2,
        link: "https://www.mcfadealers.com/en/parts-and-service/lift-truck-solutions",
        title: "Lift Truck Solutions",
        icon: "dnIconLTS"
      },
      {
        cardNumber: 3,
        link: "https://www.mcfadealers.com/en/sales-and-marketing/marketing-resources/parts-marketing",
        title: "Parts Marketing",
        icon: "dnIconPartsMarketing"
      }
    ]
  }),
  methods: {
    navigateToStep(stepName: CartStepName) {
      this.$emit("on-step-change", stepName);
    },

    getOrderType(item: IOrderResponseDto) {
      switch (item.OrderType) {
        case OrderType.None:
          return "None";
        case OrderType.ExxonMobil:
          return "Exxon Mobil";
        case OrderType.McfSap:
        case OrderType.Mcfnet:
          return "MCF";
        case OrderType.RpSap:
        case OrderType.Facts:
        case OrderType.CloudSuite:
          return "Rapidparts";
        case OrderType.PennBatteries:
          return "Penn Batteries";
        case OrderType.EnschedeWarehouse:
            return "MLE Enschede";  
        case OrderType.TriathlonBatteries:
          return "Triathlon Batteries";  
          default:
          return "Unknown";
      }
    },

    getStatusLabel(item: IOrderResponseDto) {
      switch (item.OrderStatus) {
        case OrderPlacementStatus.Success:
          return this.getContentItem("Successfully Placed");
        case OrderPlacementStatus.Pending:
        case OrderPlacementStatus.Queued:
          return this.getContentItem("Pending");
        case OrderPlacementStatus.NotPlaced:
        case OrderPlacementStatus.Failed:
          return this.getContentItem("Failed");
        default:
          return this.getContentItem("Unknown");
      }
    },

    /*
    * If we get bug reports on failed partial orders and keeping the entire cart
    * we will need to find a way to figure out what items in the order succeeded
    * and send only those id's into ShoppingCartService.RemoveCartItems endpoint.
    * At this point, looks like we'd need to cross reference the parts inside the 
    * successful orders by the order numbers. 
    */
    clearCartOnSuccess() {
      if (this.items == undefined || this.items.length === 0) {
        return;
      }
      
      for (let i = 0; i < this.items.length; i++) {
        // always clear the cart if we're offline to SAP
        if (!this.context.isOfflineToSap && (this.items[i].status === "Failed" || this.items[i].status === "Unknown")) {
          return;
        } 
      }

      this.sendClearCartRequest();
    },

    async sendClearCartRequest() {
      try {
        const params = {
          mcfCostCenterId: this.McfCostCenterId,
          accountId: this.activeAccountId,
          userId: this.user.UserId,
          shoppingCartId: this.context.cartId
        } as IEmptyCartRequest;
        await ShoppingCartService.EmptyCart(params).then((result) => {
          this.$store.dispatch("LOAD_CART_SUMMARY", { accountId: this.activeAccountId, userId: this.user.UserId });
        });
      } catch (err) {
        ErrorLogService.LogException(err);

        this.$store.commit("pushTimedMessage", {
          header: "Error emptying your cart.",
          message: "You can remove your parts manually in the shopping cart. If this continues to be a problem, please contact support.",
          IsError: true,
          seconds: 20,
        });
      }
    },

    FinishProcessingPartsStoreOrder() {
      let partsStoreOrderData = this.getRecentComponentData;

      try {
        PartsStoreOrdersService.ProcessOrderAndCleanup(
          partsStoreOrderData.fulfillmentAccountId,
          this.user.UserId,
          partsStoreOrderData.partsStoreOrderId,
          this.context.cartId
        );

        let dealerNetOrder = this.context.orderSummary[0];
        let salesOrganization = this.context.cartData.Items[0].SalesOrganization;
        let dealerCode = this.user.DealerCode;

        PartsStoreOrdersService.LinkDealerNetOrder(
          partsStoreOrderData.partsStoreOrderId,
          dealerNetOrder.Id,
          dealerNetOrder.OrderNumber,
          dealerNetOrder.OrderSource,
          dealerCode,
          salesOrganization
        );
        
        this.$store.commit("SET_RECENT_COMPONENT", {name: "", data: {}});
      } catch (err) {
        ErrorLogService.LogException(err);

        this.$store.commit("pushTimedMessage", {
          header: this.getContentItem("failedToProcessOrder"),
          IsError: true,
          seconds: 20,
        });
      }      
    }
  },
});
